import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CircleQuestionMark, LogOut, Menu, Package, X } from 'lucide-react'
import { NavLink, Route, Routes } from 'react-router-dom'

import { authKey, startLogin, useAuth, useLogout } from './api/auth'
import { onUnauthorized } from './api/client'
import { AlertBanner } from './components/AlertBanner'
import { LoginScreen } from './components/LoginScreen'
import { NotificationToggle } from './components/NotificationToggle'
import { Tutorial } from './components/Tutorial'
import { UserMenu } from './components/UserMenu'
import { de } from './i18n/de'
import { Dashboard } from './pages/Dashboard'
import { Projects } from './pages/Projects'
import { SyncStatus } from './pages/SyncStatus'

/**
 * The callback puts a readable reason here when a login fails, so it can be shown on the
 * login screen instead of vanishing into the address bar. Read once, then removed, so a
 * page reload does not resurrect a stale error.
 */
function useAuthError(): string | null {
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const message = params.get('auth_error')
    if (!message) return
    setError(message)
    params.delete('auth_error')
    const query = params.toString()
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
  }, [])

  return error
}

export default function App() {
  const auth = useAuth()
  const logout = useLogout()
  const authError = useAuthError()
  const queryClient = useQueryClient()
  const [tutorialOpen, setTutorialOpen] = useState(false)
  const closeTutorial = useCallback(() => setTutorialOpen(false), [])
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  // Escape closes the mobile menu, like every other overlay here.
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  // Offered at most once per visit. The server flag stays true until the dialog reports
  // back, and a background refetch in between must not push it in front of someone again.
  const owedTutorial = auth.data?.show_tutorial ?? false
  const offered = useRef(false)
  useEffect(() => {
    if (!owedTutorial || offered.current) return
    offered.current = true
    setTutorialOpen(true)
  }, [owedTutorial])

  // A session that expires mid-visit shows up as a 401 on some background refetch. Re-ask
  // who we are; the gate below does the rest.
  useEffect(() => {
    onUnauthorized(() => void queryClient.invalidateQueries({ queryKey: authKey }))
    return () => onUnauthorized(null)
  }, [queryClient])

  if (auth.isLoading) {
    return (
      <p className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
        {de.auth.checking}
      </p>
    )
  }

  if (auth.isError) {
    return <LoginScreen onSignIn={() => auth.refetch()} error={de.auth.unreachable} />
  }

  const state = auth.data
  const locked = state?.auth_enabled && !state.authenticated
  if (locked) {
    return (
      <LoginScreen
        onSignIn={() => startLogin(state.login_url)}
        error={authError}
        configured={state.configured}
      />
    )
  }

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
        <div className="mx-auto flex max-w-5xl items-center gap-4 px-4 py-3">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-indigo-600 dark:text-indigo-400" aria-hidden />
            <span className="font-semibold">{de.app.title}</span>
          </div>
          <nav className="hidden items-center gap-1 text-sm sm:flex">
            <Tab to="/">{de.nav.dashboard}</Tab>
            <Tab to="/projekte">{de.nav.projects}</Tab>
            <Tab to="/status">{de.nav.status}</Tab>
          </nav>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={de.nav.menu}
            title={de.nav.menu}
            className="ml-auto rounded-lg p-1.5 text-slate-600 transition hover:bg-slate-100 sm:hidden dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {menuOpen ? (
              <X className="h-5 w-5" aria-hidden />
            ) : (
              <Menu className="h-5 w-5" aria-hidden />
            )}
          </button>
          <div className="ml-auto hidden items-center gap-2 sm:flex">
            <NotificationToggle />
            <button
              type="button"
              onClick={() => setTutorialOpen(true)}
              title={de.tutorial.open}
              aria-label={de.tutorial.open}
              className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <CircleQuestionMark className="h-4 w-4" aria-hidden />
            </button>
            {state?.auth_enabled && state.user ? (
              <UserMenu user={state.user} onSignOut={() => logout.mutate()} />
            ) : null}
          </div>
        </div>

        {/* Below `sm` the tabs, help and sign-out do not fit next to the title; they live
            behind the menu button instead, as one full-width list. */}
        {menuOpen && (
          <nav
            id="mobile-menu"
            className="border-t border-slate-200 px-4 py-2 text-sm sm:hidden dark:border-slate-800"
          >
            <ul className="flex flex-col gap-1">
              {(
                [
                  ['/', de.nav.dashboard],
                  ['/projekte', de.nav.projects],
                  ['/status', de.nav.status],
                ] as const
              ).map(([to, label]) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={to === '/'}
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-2 ${
                        isActive
                          ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                </li>
              ))}
              <li className="mt-1 border-t border-slate-200 pt-1 dark:border-slate-800">
                <NotificationToggle variant="menu" />
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    closeMenu()
                    setTutorialOpen(true)
                  }}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <CircleQuestionMark className="h-4 w-4" aria-hidden />
                  {de.nav.help}
                </button>
              </li>
              {state?.auth_enabled && state.user ? (
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      closeMenu()
                      logout.mutate()
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    <LogOut className="h-4 w-4" aria-hidden />
                    <span>{de.auth.signOut}</span>
                    <span className="ml-auto truncate text-xs text-slate-400">
                      {state.user.name}
                    </span>
                  </button>
                </li>
              ) : null}
            </ul>
          </nav>
        )}
      </header>

      <AlertBanner isAdmin={state?.user?.is_admin ?? false} />

      <main>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/projekte" element={<Projects />} />
          <Route path="/status" element={<SyncStatus />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
      </main>

      {tutorialOpen && <Tutorial onClose={closeTutorial} />}
    </div>
  )
}

function Tab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `rounded-lg px-2.5 py-1.5 transition ${
          isActive
            ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
            : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
        }`
      }
    >
      {children}
    </NavLink>
  )
}
